import React from "react";
import { motion } from "framer-motion";
import { FaHome, FaStar } from "react-icons/fa";
import IconcsCard, { IconcsCardProps } from "./IconcsCard";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";

interface IconsGridProps {
  title: string;
  description: string;
  events: IconcsCardProps[];
}

// Animation variants for container
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

// Title animation variants
const titleVariants = {
  hidden: { opacity: 0, y: -30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8 },
  },
};

const IconsGrid: React.FC<IconsGridProps> = ({ title, description, events }) => {
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-pink-50 via-white to-rose-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900">
      {/* Background Effects */}
      <BgGlassmorphism />
      <div className="absolute -top-6 -left-6 w-72 h-72 bg-gradient-to-br from-pink-200/30 to-rose-200/30 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute -bottom-6 -right-6 w-96 h-96 bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-3xl"></div>

      <div className="container relative px-4 py-16 mx-auto">
        {/* Hero Section */}
        <motion.div className="text-center mb-16" variants={titleVariants} initial="hidden" animate="visible">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-pink-50 to-rose-50 rounded-full text-sm font-medium text-pink-600 mb-6">
            <FaHome className="w-4 h-4 mr-2 text-pink-500" />
            Nos lieux
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-neutral-900 dark:text-white mb-6">
            {title}
          </h2>
          <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto leading-relaxed">
            {description}
          </p>
        </motion.div>

        {/* Events Grid */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-6xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {events.map((event, index) => (
            <IconcsCard key={index} icon={event.icon} label={event.label} index={index} />
          ))}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
        >
          <div className="bg-gradient-to-r from-pink-500 via-pink-600 to-rose-500 rounded-3xl p-8 text-white max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-4">
              <FaStar className="w-6 h-6 mr-2" />
              <span className="text-lg font-semibold">Découvrez tous nos lieux</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold mb-4">Trouvez l'espace parfait</h3>
            <p className="text-lg mb-6 opacity-90">Explorez notre sélection complète d'espaces événementiels</p>
            <button className="bg-white text-pink-600 px-8 py-4 rounded-full font-semibold hover:scale-105 transform transition-all duration-300 shadow-lg">
              Voir tous les lieux
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default IconsGrid;
